import React, { useCallback, useEffect, useState } from "react";
import DateBox from "devextreme-react/date-box";
import { Button } from "devextreme-react/button";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../pages/FundingDashboard.css";
import "./KFactorCalc.css";
import { baseUrl } from "../../hooks/useFetch";
import { fmtISO } from "../../utils/dateUtils";
import { formatNegativeValue } from "../../utils/formatUtils";

/* ── types ─────────────────────────────────────────────────── */
interface KFactorRow {
  measure: string;
  combined: number;
  cfpCommodities: number;
  cfpEnergy: number;
  cfpTrading: number;
  cfpPathway: number;
  cfpServices: number;
}

interface ApiRecord {
  cob_date: string;
  legal_entity: string;
  measure: string;
  value: number | null;
}

interface ApiResponse {
  cob_date: string;
  comparison_date: string;
  data: ApiRecord[];
  comparison_data: ApiRecord[];
}

/* ── helpers ───────────────────────────────────────────────── */
const ENTITY_KEY_MAP: Record<string, keyof Omit<KFactorRow, "measure">> = {
  combined: "combined",
  "cfp commodities": "cfpCommodities",
  "cfp energy": "cfpEnergy",
  "cfp trading": "cfpTrading",
  "cfp pathway": "cfpPathway",
  "cfp services": "cfpServices",
};

function pivotRecords(records: ApiRecord[]): KFactorRow[] {
  const byMeasure: Record<string, KFactorRow> = {};
  for (const r of records) {
    if (!byMeasure[r.measure]) {
      byMeasure[r.measure] = {
        measure: r.measure, combined: 0, cfpCommodities: 0,
        cfpEnergy: 0, cfpTrading: 0, cfpPathway: 0, cfpServices: 0,
      };
    }
    const key = ENTITY_KEY_MAP[(r.legal_entity || "").toLowerCase()];
    if (key) byMeasure[r.measure][key] = r.value ?? 0;
  }
  return Object.values(byMeasure);
}

function computeChanges(cobRows: KFactorRow[], compRows: KFactorRow[]): KFactorRow[] {
  const compMap: Record<string, KFactorRow> = {};
  for (const r of compRows) compMap[r.measure] = r;

  return cobRows.map(row => {
    const c = compMap[row.measure];
    if (!c) return row;
    return {
      measure: row.measure,
      combined: row.combined - c.combined,
      cfpCommodities: row.cfpCommodities - c.cfpCommodities,
      cfpEnergy: row.cfpEnergy - c.cfpEnergy,
      cfpTrading: row.cfpTrading - c.cfpTrading,
      cfpPathway: row.cfpPathway - c.cfpPathway,
      cfpServices: row.cfpServices - c.cfpServices,
    };
  });
}

const numCell = (cellInfo: any) => formatNegativeValue(cellInfo.value);

const API_BASE = `${baseUrl}/compliance`;

const COL_FIELDS: { dataField: keyof KFactorRow; caption: string }[] = [
  { dataField: "measure",        caption: "Measure" },
  { dataField: "combined",       caption: "Combined" },
  { dataField: "cfpCommodities", caption: "CFP Commodities" },
  { dataField: "cfpEnergy",      caption: "CFP Energy" },
  { dataField: "cfpTrading",     caption: "CFP Trading" },
  { dataField: "cfpPathway",     caption: "CFP Pathway" },
  { dataField: "cfpServices",    caption: "CFP Services" },
];

const HEADERS = COL_FIELDS.map(c => c.caption);
const DATA_FIELDS = COL_FIELDS.map(c => c.dataField);

/* ── grid sub-component ────────────────────────────────────── */
const KFactorGrid: React.FC<{ title: string; rows: KFactorRow[] }> = ({ title, rows }) => (
  <div className="card">
    <div className="cardTitle">{title}</div>
    <DataGrid
      className="dx-compact-grid"
      dataSource={rows}
      showBorders
      rowAlternationEnabled
      width="100%"
      columnAutoWidth
    >
      <Paging enabled={false} />
      {COL_FIELDS.map(({ dataField, caption }) => (
        <Column
          key={dataField}
          dataField={dataField}
          caption={caption}
          alignment={dataField === "measure" ? "left" : "right"}
          cellRender={dataField === "measure" ? undefined : numCell}
        />
      ))}
    </DataGrid>
  </div>
);

/* ── main component ───────────────────────────────────────── */
const KFactorCalc: React.FC = () => {
  const [cobDate,        setCobDate]        = useState<Date | null>(null);
  const [comparisonDate, setComparisonDate] = useState<Date | null>(null);
  const [loading,        setLoading]        = useState(false);
  const [cobData,        setCobData]        = useState<KFactorRow[]>([]);
  const [changeData,     setChangeData]     = useState<KFactorRow[]>([]);

  /* shared fetch + state update */
  const applyResponse = useCallback((resp: ApiResponse) => {
    const pivotedCob = pivotRecords(resp.data);
    setCobData(pivotedCob);
    if (resp.comparison_data?.length) {
      setChangeData(computeChanges(pivotedCob, pivotRecords(resp.comparison_data)));
    } else {
      setChangeData([]);
    }
  }, []);

  /* initial load — single call, no params → backend defaults to latest COB + prev year end */
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/k_factors`, { signal: ac.signal });
        const resp: ApiResponse = await r.json();
        setCobDate(new Date(resp.cob_date));
        setComparisonDate(new Date(resp.comparison_date));
        applyResponse(resp);
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* refetch when user changes either date picker */
  const [initialised, setInitialised] = useState(false);
  useEffect(() => {
    if (!cobDate || !comparisonDate) return;
    if (!initialised) { setInitialised(true); return; } // skip the set from initial load
    const ac = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const url = `${API_BASE}/k_factors?cob_date=${encodeURIComponent(fmtISO(cobDate) ?? "")}&comparison_date=${encodeURIComponent(fmtISO(comparisonDate) ?? "")}`;
        const r = await fetch(url, { signal: ac.signal });
        const resp: ApiResponse = await r.json();
        applyResponse(resp);
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [cobDate, comparisonDate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── export helpers ───────────────────────────────────────── */
  const fmtNum = (v: number | string | null | undefined): string => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    const abs = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
    return n < 0 ? `(${abs})` : abs;
  };

  function kfToRows(rows: KFactorRow[]): string[][] {
    return rows.map(r => DATA_FIELDS.map(f => f === "measure" ? r.measure : fmtNum(r[f])));
  }

  const exportExcel = async (): Promise<void> => {
    const wb = new ExcelJS.Workbook();
    const headerFill: any = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
    const headerFont: any = { bold: true, color: { argb: "FFFFFFFF" } };

    const writeSheet = (name: string, data: KFactorRow[]) => {
      const ws = wb.addWorksheet(name);
      for (let c = 0; c < HEADERS.length; c++) ws.getColumn(c + 1).width = c === 0 ? 30 : 18;

      const hdr = ws.addRow(HEADERS);
      hdr.eachCell(cell => { cell.fill = headerFill; cell.font = headerFont; });

      data.forEach(row => {
        const r = ws.addRow(DATA_FIELDS.map(f => row[f]));
        r.eachCell((cell, col) => {
          cell.alignment = { horizontal: col === 1 ? "left" : "right" };
          if (typeof cell.value === "number") cell.numFmt = "#,##0;[Red]-#,##0";
        });
        if (/total|funds|buffer/i.test(row.measure)) {
          r.font = { bold: true };
          r.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D9D9D9" } };
        }
      });
    };

    writeSheet(`As of CoB ${fmtISO(cobDate) || ""}`, cobData);
    if (changeData.length) writeSheet(`Change vs ${fmtISO(comparisonDate) || ""}`, changeData);

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `KFactor_Report_${fmtISO(cobDate) || "export"}.xlsx`);
  };

  const exportPdf = (): void => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const margin = 30;
    const fullWidth = doc.internal.pageSize.getWidth() - margin * 2;

    doc.setFontSize(14);
    doc.text("K-Factor Report", margin, 30);

    const addTable = (title: string, body: string[][], startY: number): number => {
      doc.setFontSize(9);
      doc.text(title, margin, startY);
      (doc as any).autoTable({
        startY: startY + 6,
        head: [HEADERS],
        body,
        tableWidth: fullWidth,
        margin: { left: margin },
        styles: { fontSize: 7, cellPadding: 2.5, valign: "middle" },
        headStyles: { fontStyle: "bold", fontSize: 6.5 },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" },
        },
        didParseCell: (cellData: any) => {
          if (cellData.section === "body") {
            const txt = (cellData.cell.text || []).join("");
            if (txt.includes("(") && txt.includes(")")) cellData.cell.styles.textColor = [255, 0, 0];
          }
        },
      });
      return (doc as any).lastAutoTable.finalY;
    };

    let y = addTable(`As of CoB ${fmtISO(cobDate) || ""}`, kfToRows(cobData), 46) + 18;
    if (changeData.length) addTable(`Change vs CoB ${fmtISO(comparisonDate) || ""}`, kfToRows(changeData), y);

    doc.save(`KFactor_Report_${fmtISO(cobDate) || "export"}.pdf`);
  };

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div>
      {/* Command bar */}
      <div className="topBar">
        <div className="dateBoxWrap">
          <div className="dateLabel">CoB</div>
          <DateBox value={cobDate} type="date" displayFormat="dd/MM/yyyy"
            onValueChanged={(e: any) => setCobDate(e.value)} width={220}
            stylingMode="underlined"
            elementAttr={{ class: "kf-datebox" }} />
        </div>
        <div className="dateBoxWrap">
          <div className="dateLabel">Comparison</div>
          <DateBox value={comparisonDate} type="date" displayFormat="dd/MM/yyyy"
            onValueChanged={(e: any) => setComparisonDate(e.value)} width={220}
            stylingMode="underlined"
            elementAttr={{ class: "kf-datebox" }} />
        </div>
        {loading && <div className="loadingPill">Loading…</div>}
        <div className="exportButtons">
          <Button text="Export Excel" stylingMode="contained" type="default"
            onClick={exportExcel} disabled={loading} height={30}
            style={{ backgroundColor: "#28a745", color: "white", borderRadius: "4px", border: "none" }} />
          <Button text="Export PDF" stylingMode="contained" type="normal"
            onClick={exportPdf} disabled={loading} height={30}
            style={{ backgroundColor: "#d9534f", color: "white", borderRadius: "4px", border: "none" }} />
        </div>
      </div>

      {/* Content */}
      <div className="fundingContent">
        <KFactorGrid title={`As of CoB ${fmtISO(cobDate) || ""} — Figures in €`} rows={cobData} />
        {changeData.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <KFactorGrid title={`Change vs CoB ${fmtISO(comparisonDate) || ""}`} rows={changeData} />
          </div>
        )}
      </div>
    </div>
  );
};

export default KFactorCalc;
